import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import cn from 'classnames';

import './styles.scss';

type Props = {
  pinned: boolean;
  onTogglePin: () => void;
  onRemove: () => void;
}

export const FavoriteCardMenu = ({pinned, onTogglePin, onRemove}: Props): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const node = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (node.current && !node.current.contains(event.target as HTMLElement)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  function handleToggle() {
    setIsOpen(!isOpen);
  }

  function handlePinClick() {
    setIsOpen(false);
    onTogglePin();
  }

  function handleRemoveClick() {
    setIsOpen(false);
    onRemove();
  }

  return (
    <div className="favorite-card-menu" ref={node}>
      <button
        type="button"
        className="favorite-card-menu__trigger"
        onClick={handleToggle}
        aria-label="Card options"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <circle cx="10" cy="4" r="1.8"/>
          <circle cx="10" cy="10" r="1.8"/>
          <circle cx="10" cy="16" r="1.8"/>
        </svg>
      </button>
      <div className={cn('favorite-card-menu__panel', {'favorite-card-menu__panel--open': isOpen})} role="menu">
        <button type="button" className="favorite-card-menu__item" onClick={handlePinClick} role="menuitem">
          {pinned ? (
            <FormattedMessage id="gritx.backpack.menu.unpin" defaultMessage="Unpin"/>
          ) : (
            <FormattedMessage id="gritx.backpack.menu.pin" defaultMessage="Pin"/>
          )}
        </button>
        <button
          type="button"
          className="favorite-card-menu__item favorite-card-menu__item--danger"
          onClick={handleRemoveClick}
          role="menuitem"
        >
          <FormattedMessage id="gritx.backpack.menu.remove" defaultMessage="Remove from Favorites"/>
        </button>
      </div>
    </div>
  );
};
